import { useCallback, useEffect, useRef, useState } from "react";
import {
  isInFlight,
  isRetryablePollFailure,
  MigrationStatus,
} from "../../api/migrationsApi";
import { NO_DETAIL_MESSAGE } from "../../api/apiUtils";

/**
 * The poll-then-apply state machine every migration-job modal needs (AKL-813).
 *
 * Lifted from `MigrateFromWildlifeInsights` so the CSV import modal — and later the
 * locations and deployments imports — drive a job without copying sixty lines of polling.
 * Behaviour is the WI modal's, unchanged: poll every two seconds while the server is still
 * working, back off on a transient failure and give up after three, stop at once on a
 * definitive 4xx, fire `onApplied` exactly once per job, and clean up the timer on unmount.
 * The WI modal itself still runs its inline copy; re-pointing it at this hook is a follow-up.
 *
 * Generic over the job type because a CSV import job and a WI migration job are the same row
 * with different plan shapes; the hook only reads `guid` and `status`.
 */

/** How often to re-check a job the server is still working on. */
export const POLL_INTERVAL_MS = 2000;

/**
 * Consecutive poll failures tolerated before asking the user.
 * A large file takes a while to walk and will occasionally hit a dropped connection; three
 * strikes with backoff (2s, 4s, 8s) distinguishes a blip from an unreachable server.
 */
export const MAX_POLL_FAILURES = 3;

export type UseMigrationJobOptions<TJob> = {
  /** False while the modal is closed: no polling runs. */
  enabled: boolean;
  /** Fetch the job by guid — the polled call, expected to bypass the global error toast. */
  fetchJob: (guid: string) => Promise<TJob>;
  /** Apply the job by guid — the one call that writes. */
  applyJob: (guid: string) => Promise<TJob>;
  /**
   * Called once when the job reaches "applied". Kept current in a ref, so the caller may
   * pass a fresh closure on every render and the LATEST one runs — never a stale one
   * captured when polling began.
   */
  onApplied?: (job: TJob) => void;
  /** Shown when polling gives up; the job may still be running server-side. */
  pollFailureText: string;
  /** Shown when the apply request fails without a usable detail from the server. */
  applyFailureText: string;
};

export type UseMigrationJobResult<TJob> = {
  job: TJob | null;
  setJob: (job: TJob | null) => void;
  /** True while a request the user started (upload or apply) is in flight. */
  busy: boolean;
  setBusy: (busy: boolean) => void;
  /** The last upload/apply error, or null. */
  error: string | null;
  setError: (error: string | null) => void;
  /** Set when polling gave up; null while polling is healthy or not running. */
  pollFailure: string | null;
  /** Apply the current job. No-op without one. */
  apply: () => Promise<void>;
  /** Forget the job and every error, e.g. to let the user choose another file. */
  reset: () => void;
  /** True while the server is working on the job and polling is healthy. */
  working: boolean;
};

/**
 * The detail of a failed request as text, read from the shape the axios interceptor
 * produces: a rejected 4xx is `{ message, status, data }`, never `err.response`, and a 422
 * `detail` is a list of `{ loc, msg, type }`. Falls back to `message`, except the
 * interceptor's "Request error" placeholder, which means the server gave no detail.
 * @param err the rejected error
 * @param fallback the sentence to show when the error carries nothing usable
 * @returns the text to display
 */
export const errorDetail = (err: unknown, fallback: string): string => {
  const shaped = err as
    | { data?: { detail?: unknown }; message?: unknown }
    | null
    | undefined;
  const detail = shaped?.data?.detail;
  if (typeof detail === "string" && detail.trim() !== "") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) =>
        entry && typeof entry === "object"
          ? (entry as { msg?: unknown }).msg
          : undefined,
      )
      .filter((msg): msg is string => typeof msg === "string" && msg !== "");
    if (messages.length > 0) return messages.join("; ");
  }
  // The interceptor sets `message` to its "Request error" placeholder when the body had no
  // detail; that is not something to show a user (#831 review). A real message, such as the
  // interceptor's translated timeout sentence, still wins over the fallback.
  const message = shaped?.message;
  return typeof message === "string" &&
    message !== "" &&
    message !== NO_DETAIL_MESSAGE
    ? message
    : fallback;
};

/** The caller's already-translated sentences for `uploadErrorMessage`. */
export type UploadErrorTexts = {
  /** The file could not be read (a 400). The API's reason, when it gave one, follows it. */
  unreadable: string;
  /** Shown for a 429 that carries no detail of its own. */
  inProgress: string;
  /** A server failure, a timeout, a network failure, or anything unrecognised. */
  retry: string;
};

/**
 * The API's own detail as text, or null when it gave none: a non-blank string, or a 422
 * list joined by its messages. The interceptor's "Request error" placeholder is not a detail.
 * @param err the rejected error, in the interceptor's `{ message, status, data }` shape
 * @returns the detail text, or null
 */
const usableDetail = (err: unknown): string | null => {
  const detail = (err as { data?: { detail?: unknown } } | null | undefined)
    ?.data?.detail;
  if (typeof detail === "string") {
    const trimmed = detail.trim();
    return trimmed === "" || trimmed === NO_DETAIL_MESSAGE ? null : trimmed;
  }
  if (Array.isArray(detail)) {
    const joined = errorDetail(err, "");
    return joined === "" || joined === NO_DETAIL_MESSAGE ? null : joined;
  }
  return null;
};

/**
 * What to tell the user when uploading a file to `POST /migrations` fails (AKL-1040).
 *
 * Keyed on the status the API answers with (`MigrationService.create_migration`), because
 * each means something different to the user, and blaming the file for all of them told a
 * user their bundle was broken when another migration was simply still running:
 *
 * - **429** — a migration is already in flight: the API's detail (it says when the job will
 *   clear, AKL-1039), else `inProgress`.
 * - **400** — the file cannot be read (not a zip or CSV, a required CSV missing, empty):
 *   `unreadable`, followed by the API's reason when it gave one.
 * - **any other 4xx** (403 permission, 413 too large, 422 options) — the API's detail, else
 *   `retry`.
 * - **5xx, timeout, network failure, or no status at all** — `retry`. The server did not
 *   judge the file, so the file is not blamed.
 *
 * Reads the interceptor's shape (`{ message, status, data }`), never `err.response`.
 * @param err the rejected error
 * @param texts the caller's translated sentences
 * @returns the text to display
 */
export const uploadErrorMessage = (
  err: unknown,
  texts: UploadErrorTexts,
): string => {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  if (typeof status !== "number" || status >= 500) return texts.retry;
  const detail = usableDetail(err);
  if (status === 429) return detail ?? texts.inProgress;
  if (status === 400)
    return detail ? `${texts.unreadable} ${detail}` : texts.unreadable;
  if (status >= 400) return detail ?? texts.retry;
  return texts.retry;
};

/**
 * Drive one migration job: poll it while the server works, apply it on request, and tell
 * the caller once when it has been applied.
 * @param options the job API, the caller's callbacks and the failure wording
 * @returns the job, its request state, and the actions on it
 */
export function useMigrationJob<
  TJob extends { guid: string; status: MigrationStatus },
>({
  enabled,
  fetchJob,
  applyJob,
  onApplied,
  pollFailureText,
  applyFailureText,
}: UseMigrationJobOptions<TJob>): UseMigrationJobResult<TJob> {
  const [job, setJob] = useState<TJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollFailure, setPollFailure] = useState<string | null>(null);

  // Kept in a ref as well as the effect's own `stopped` flag so an upload or apply that
  // resolves after unmount does not set state on a component that is gone.
  const cancelled = useRef(false);
  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  // The latest `onApplied`, so the effect below never calls a stale closure — the caller's
  // handler typically closes over a search query that may have changed since polling began.
  // Written from an effect, not during render, so a discarded or doubled render cannot
  // leave the ref pointing at a callback that was never committed. Declared before the
  // "applied" effect so that, within one commit, the ref is current when that effect reads it.
  const onAppliedRef = useRef(onApplied);
  useEffect(() => {
    onAppliedRef.current = onApplied;
  }, [onApplied]);

  /**
   * Poll a job the server is still working on until it settles.
   *
   * Deliberately not a `setInterval`: overlapping requests on a slow connection would queue
   * up behind each other and the backoff below could not apply.
   */
  useEffect(() => {
    if (!enabled || job === null || !isInFlight(job.status)) return;

    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const tick = async () => {
      try {
        const next = await fetchJob(job.guid);
        if (stopped || cancelled.current) return;
        failures = 0;
        setPollFailure(null);
        setJob(next);
        if (isInFlight(next.status)) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (stopped || cancelled.current) return;
        failures += 1;
        if (!isRetryablePollFailure(err) || failures >= MAX_POLL_FAILURES) {
          setPollFailure(pollFailureText);
          return;
        }
        // Exponential backoff: 2s, 4s, 8s.
        timer = setTimeout(tick, POLL_INTERVAL_MS * 2 ** failures);
      }
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, job, fetchJob, pollFailureText]);

  const apply = useCallback(async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const next = await applyJob(job.guid);
      if (cancelled.current) return;
      setJob(next);
    } catch (err) {
      if (cancelled.current) return;
      setError(errorDetail(err, applyFailureText));
    } finally {
      if (!cancelled.current) setBusy(false);
    }
  }, [job, applyJob, applyFailureText]);

  const reset = useCallback(() => {
    setJob(null);
    setBusy(false);
    setError(null);
    setPollFailure(null);
  }, []);

  // Fires once per job, when it reaches "applied", so the caller can refresh what it shows.
  const notified = useRef<string | null>(null);
  useEffect(() => {
    if (job?.status === "applied" && notified.current !== job.guid) {
      notified.current = job.guid;
      onAppliedRef.current?.(job);
    }
  }, [job]);

  const working =
    job !== null && isInFlight(job.status) && pollFailure === null;

  return {
    job,
    setJob,
    busy,
    setBusy,
    error,
    setError,
    pollFailure,
    apply,
    reset,
    working,
  };
}
